import './accordion.scss';

import React, { forwardRef, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSpring, animated } from 'react-spring';

import { Card, Typography, Button } from '../base';
import Icon, { ICON_TYPE } from '../icon';
import useWindowResizeEffect from '../hooks/useWindowResizeEffect';

const Accordion = forwardRef((props, ref) => {
  const [activeIndex, setActiveIndex] = useState(props.activeIndex);

  const className = classNames({
    accordion: true,
    [props.className]: props.className,
  });

  const handleOnSelect = index => {
    setActiveIndex(value => (value !== index ? index : null));
  };

  return (
    <div ref={ref} className={className}>
      {props.title && (
        <AccordionTitle>
          {/* h2 so item h3s nest under it; .accordion-title keeps the h3 visual size */}
          <Typography.H2 className="accordion-title">{props.title}</Typography.H2>
        </AccordionTitle>
      )}
      {React.Children.map(props.children, (child, index) => ({
        ...child,
        props: {
          ...child.props,
          show: index === activeIndex,
          onSelect: () => handleOnSelect(index),
        },
      }))}
    </div>
  );
});

Accordion.propTypes = {
  title: PropTypes.string,
  activeIndex: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node,
};

const AccordionTitle = forwardRef((props, ref) => (
  <Card ref={ref} className={props.className}>
    <Card.Header className="card-header-title">{props.children}</Card.Header>
  </Card>
));

AccordionTitle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const AccordionItem = forwardRef((props, ref) => {
  const cardBodyRef = useRef();
  const [bodyHeight, setBodyHeight] = useState(0);

  const headerClassName = classNames({
    active: props.show,
  });

  const handleOnItemClick = e => {
    e.preventDefault();

    props.onSelect();
  };

  const updateElementsHeight = () => {
    const { height } = cardBodyRef.current.getBoundingClientRect();
    setBodyHeight(height);
  };

  useEffect(() => {
    updateElementsHeight();
  }, [props.show]);

  useWindowResizeEffect(updateElementsHeight);

  let spring;
  if (props.show) {
    spring = { height: bodyHeight, from: { height: 0 } };
  } else {
    spring = { height: 0, from: { height: bodyHeight } };
  }

  return (
    <Card ref={ref} className={props.className}>
      <Card.Header className={headerClassName}>
        {/* h3 for extractability (FAQPage); .accordion-item-title keeps the h5 visual size */}
        <Typography.H3 className="accordion-item-title">
          <Button type="link" onClick={handleOnItemClick}>
            {props.title}
          </Button>
          <Icon type={ICON_TYPE.BACK} />
        </Typography.H3>
      </Card.Header>
      <animated.div className="collapse show" style={useSpring(spring)}>
        <Card.Body ref={cardBodyRef}>{props.children}</Card.Body>
      </animated.div>
    </Card>
  );
});

AccordionItem.propTypes = {
  title: PropTypes.string,
  show: PropTypes.bool,
  className: PropTypes.string,
  onSelect: PropTypes.func,
  children: PropTypes.node,
};

Accordion.Item = AccordionItem;

export default Accordion;
